import {
  ArrowRight,
  CheckCircle2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  FileText,
  Gift,
  Globe2,
  Headphones,
  Lightbulb,
  Lock,
  Mail,
  AudioWaveform,
  Music2,
  Pause,
  PenLine,
  Play,
  PlayCircle,
  ReceiptText,
  ShieldCheck,
  ShoppingCart,
  SlidersHorizontal,
  Sparkles,
  Trash2,
} from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  addOns,
  defaultLocale,
  genres,
  getLocalizedContent,
  isLocaleCode,
  localeOptions,
  localeToLanguageTag,
  type LocaleCode,
} from "./content";
import { analyticsConsent, clearAnalyticsConsent, setAnalyticsConsent, trackTuneTailorEvent } from "./analytics";
import { clearSavedSongs, readSavedSongs, saveSongs } from "./cart-storage";
import { submitCheckoutRequest } from "./checkout-presentation";
import { isStorefrontRoute, localizedRoute, localeFromPath, sectionForPath, stripLocalePrefix } from "./routes";
import {
  createCheckoutRequest,
  formatMoney,
  getProductByHandle,
  getSignatureProduct,
  initialCatalog,
  loadCatalog,
  selectedVariant,
  subtotalForSongs,
} from "./shopify";
import type { AddOnKey, CartSong, ProductCatalog } from "./types";

const emptyDraft = {
  title: "",
  genre: "Surprise me",
  story: "",
  addOns: [] as AddOnKey[],
};

const maxStoryLength = 12_000;

const heroWaveform = [18, 9, 22, 12, 31, 16, 26, 10, 34, 20, 12, 28, 15, 36, 18, 24, 10, 20, 30, 13, 22, 38, 16, 11, 27, 33, 14, 18, 29, 12, 24, 37, 15, 20, 12, 31, 17, 25, 10, 21, 34, 13, 19, 28, 16, 11, 23, 35];
const primaryGenreChoices = ["Rock", "Acoustic", "Pop", "Country"];

type CatalogStatus = "syncing" | "storefrontApi" | "snapshot";

export function App() {
  const [locale, setLocale] = useState<LocaleCode>(() => detectLocale());
  const [catalog, setCatalog] = useState<ProductCatalog>(() => initialCatalog());
  const [catalogStatus, setCatalogStatus] = useState<CatalogStatus>("syncing");
  const [songs, setSongs] = useState<CartSong[]>(() => readSavedSongs());
  const [draft, setDraft] = useState(emptyDraft);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formError, setFormError] = useState("");
  const [checkoutError, setCheckoutError] = useState("");
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [consent, setConsent] = useState(() => analyticsConsent());
  const [showMobileCheckout, setShowMobileCheckout] = useState(false);
  const [activeSampleIndex, setActiveSampleIndex] = useState(0);
  const [isHeroSamplePlaying, setIsHeroSamplePlaying] = useState(false);
  const heroAudioRef = useRef<HTMLAudioElement | null>(null);
  const prefersReducedMotion = useReducedMotion();
  const [sampleRailRef, sampleRailApi] = useEmblaCarousel({
    align: "start",
    containScroll: "trimSnaps",
    dragFree: true,
  });

  const t = useMemo(() => getLocalizedContent(locale), [locale]);
  const languageTag = localeToLanguageTag(locale);

  useEffect(() => {
    loadCatalog()
      .then((nextCatalog) => {
        setCatalog(nextCatalog);
        setCatalogStatus(nextCatalog.source === "storefront-api" ? "storefrontApi" : "snapshot");
      })
      .catch(() => {
        setCatalogStatus("snapshot");
      });
  }, []);

  useEffect(() => {
    saveSongs(songs);
  }, [songs]);

  useEffect(() => {
    try {
      localStorage.setItem("tunetailor:locale", locale);
    } catch {
      // Locale still applies to the current page when storage is unavailable.
    }
    document.documentElement.lang = languageTag;
    document.title = t.meta.title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", t.meta.description);
  }, [languageTag, locale, t.meta.description, t.meta.title]);

  useEffect(() => {
    sampleRailApi?.scrollTo(activeSampleIndex);
  }, [activeSampleIndex, sampleRailApi]);

  useEffect(() => {
    const heroAction = document.querySelector(".hero-cta");
    if (!heroAction || typeof IntersectionObserver === "undefined") {
      setShowMobileCheckout(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setShowMobileCheckout(!entry.isIntersecting),
      { threshold: 0.2 },
    );
    observer.observe(heroAction);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let scrollTimer: number | undefined;

    const syncFromRoute = () => {
      const routedLocale = localeFromPath();
      if (routedLocale) {
        setLocale(routedLocale);
      }

      const hashSection = window.location.hash ? decodeURIComponent(window.location.hash.slice(1)) : null;
      const sectionId = hashSection || sectionForPath(window.location.pathname);
      if (sectionId) {
        const scrollToSection = () => {
          document.getElementById(sectionId)?.scrollIntoView({ block: "start" });
        };
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(scrollToSection);
        });
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(scrollToSection, 150);
      }
    };

    syncFromRoute();
    window.addEventListener("popstate", syncFromRoute);
    window.addEventListener("load", syncFromRoute, { once: true });
    return () => {
      window.clearTimeout(scrollTimer);
      window.removeEventListener("popstate", syncFromRoute);
      window.removeEventListener("load", syncFromRoute);
    };
  }, []);

  const signatureVariant = selectedVariant(getSignatureProduct(catalog));
  const subtotal = useMemo(() => subtotalForSongs(songs, catalog), [songs, catalog]);
  const currencyCode = signatureVariant?.price.currencyCode || "USD";
  const sampleTracks = t.sampleTracks;
  const activeSample = sampleTracks[activeSampleIndex] || sampleTracks[0];
  const heroSample = sampleTracks[0];
  const heroTitle = locale === "en" ? "Your story,|turned into a song" : t.hero.title;
  const proofIcons = [Headphones, ReceiptText, ShieldCheck];
  const heroStats = t.hero.proof.map((proof, index) => ({ ...proof, icon: proofIcons[index] }));
  const signaturePrice = signatureVariant ? Number(signatureVariant.price.amount) : 49.95;
  const draftAddOnSubtotal = draft.addOns.reduce((sum, key) => {
    const addOn = addOns.find((candidate) => candidate.key === key);
    const variant = selectedVariant(addOn ? getProductByHandle(catalog, addOn.handle) : undefined);
    return sum + (variant ? Number(variant.price.amount) : 0);
  }, 0);
  const draftSubtotal = signaturePrice + draftAddOnSubtotal;
  const draftIsBlank = !draft.title.trim() && !draft.story.trim() && draft.genre === emptyDraft.genre && draft.addOns.length === 0;
  const stickyAmount = songs.length > 0 && draftIsBlank ? subtotal : draftSubtotal;
  const stickyActionLabel = songs.length > 0 && draftIsBlank ? t.actions.proceedToCheckout : "Review & Continue";
  const draftMilestones = [
    { label: t.builder.titleLabel, complete: draft.title.trim().length > 0 },
    { label: t.builder.genreLabel, complete: genres.includes(draft.genre) },
    { label: t.builder.storyLabel, complete: draft.story.trim().length > 0 },
    { label: t.builder.optionalAddOns, complete: draft.addOns.length > 0 },
  ];
  const draftProgress = Math.round(
    (draftMilestones.filter((milestone) => milestone.complete).length / draftMilestones.length) * 100,
  );
  const revealMotion = prefersReducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 28 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.14 },
        transition: { duration: 0.58 },
      };

  function updateDraft(field: "title" | "genre" | "story", value: string) {
    setDraft((current) => ({ ...current, [field]: value }));
  }

  function changeLocale(nextLocale: string) {
    if (!isLocaleCode(nextLocale)) {
      return;
    }

    setLocale(nextLocale);
    window.history.pushState(
      {},
      "",
      localizedRoute(nextLocale, `${window.location.pathname}${window.location.search}${window.location.hash}`),
    );
  }

  function chooseAnalyticsConsent(nextConsent: "granted" | "denied") {
    setAnalyticsConsent(nextConsent);
    setConsent(nextConsent);
  }

  function reopenAnalyticsChoices() {
    clearAnalyticsConsent();
    setConsent("unknown");
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        document.getElementById("privacy-choices")?.scrollIntoView({ block: "start" });
      });
    });
  }

  function toggleAddOn(key: AddOnKey) {
    setDraft((current) => ({
      ...current,
      addOns: current.addOns.includes(key)
        ? current.addOns.filter((candidate) => candidate !== key)
        : [...current.addOns, key],
    }));
  }

  function submitSong(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormError("");

    const title = draft.title.trim();
    const story = draft.story.trim();
    if (!title) {
      setFormError(t.errors.titleRequired);
      return;
    }
    if (!genres.includes(draft.genre)) {
      setFormError(t.errors.genreInvalid);
      return;
    }
    if (draft.genre === "Other" && story.length < 20) {
      setFormError(t.errors.otherGenre);
      return;
    }
    if (!story) {
      setFormError(t.errors.storyRequired);
      return;
    }

    const song: CartSong = {
      id: editingId || createSongId(),
      title,
      genre: draft.genre,
      story,
      addOns: draft.addOns,
    };

    setSongs((current) => {
      if (!editingId) {
        return [...current, song];
      }
      return current.map((candidate) => (candidate.id === editingId ? song : candidate));
    });
    setDraft(emptyDraft);
    setEditingId(null);
    trackTuneTailorEvent("tunetailor_cta_clicked", { target: "song_review_added" });
    document.querySelector("#review")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function editSong(song: CartSong) {
    setDraft({
      title: song.title,
      genre: song.genre,
      story: song.story,
      addOns: song.addOns,
    });
    setEditingId(song.id);
    setFormError("");
    document.querySelector("#song-builder")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function removeSong(songId: string) {
    setSongs((current) => current.filter((song) => song.id !== songId));
    if (editingId === songId) {
      setDraft(emptyDraft);
      setEditingId(null);
    }
  }

  function showPreviousSample() {
    setActiveSampleIndex((current) => (current === 0 ? sampleTracks.length - 1 : current - 1));
  }

  function showNextSample() {
    setActiveSampleIndex((current) => (current + 1) % sampleTracks.length);
  }

  function toggleHeroSample() {
    const player = heroAudioRef.current;
    if (!player) {
      return;
    }

    if (player.paused) {
      void player
        .play()
        .then(() => trackTuneTailorEvent("tunetailor_sample_played", { track: heroSample.title }))
        .catch(() => setIsHeroSamplePlaying(false));
      return;
    }

    player.pause();
  }

  function handleStickyAction() {
    if (songs.length > 0 && draftIsBlank) {
      void checkout();
      return;
    }

    document.querySelector<HTMLFormElement>("#song-brief-form")?.requestSubmit();
  }

  async function checkout() {
    setCheckoutError("");
    setIsCheckingOut(true);
    try {
      const checkoutRequest = await createCheckoutRequest(songs, catalog, locale);
      trackTuneTailorEvent("checkout_started", {
        songCount: songs.length,
        addOnCount: songs.reduce((count, song) => count + song.addOns.length, 0),
        catalog: catalog.source,
        locale,
      });
      clearSavedSongs();
      await submitCheckoutRequest(checkoutRequest, {
        onClose: () => setIsCheckingOut(false),
        onComplete: () => {
          setSongs([]);
          setCheckoutError("");
          setIsCheckingOut(false);
        },
        onError: () => {
          setCheckoutError(t.errors.checkoutFailed);
          setIsCheckingOut(false);
        },
      });
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : t.errors.checkoutFailed);
      setIsCheckingOut(false);
    }
  }

  const isNotFound =
    stripLocalePrefix(window.location.pathname) === "/404" || !isStorefrontRoute(window.location.pathname);
  if (isNotFound) {
    return (
      <main className="not-found">
        <a className="brand" href={localizedRoute(locale, "/")} aria-label="TuneTailor home">
          <span className="brand-mark" aria-hidden="true">TT</span>
          <span>TuneTailor</span>
        </a>
        <p className="kicker">404</p>
        <h1>Page not found</h1>
        <p>The page may have moved. Return home or start a custom song.</p>
        <div>
          <a className="button button--secondary" href={localizedRoute(locale, "/")}>{t.nav.home}</a>
          <a className="button button--primary" href={localizedRoute(locale, "/products/custom-song#song-builder")}>{t.actions.createSong}</a>
        </div>
      </main>
    );
  }

  return (
    <>
      <header className="site-header">
        <a className="brand" href={localizedRoute(locale, "/")} aria-label="TuneTailor home">
          <span className="brand-mark" aria-hidden="true">
            TT
          </span>
          <span>TuneTailor</span>
        </a>
        <nav className="main-nav" aria-label="Primary navigation">
          <a href={localizedRoute(locale, "/pages/how-it-works")}>{t.nav.howItWorks}</a>
          <a href={localizedRoute(locale, "/pages/occasions")}>{t.nav.occasions}</a>
          <a href={localizedRoute(locale, "/pages/examples")}>{t.nav.examples}</a>
          <a href={localizedRoute(locale, "/pages/faq")}>{t.nav.faq}</a>
          <a href={localizedRoute(locale, "/products/custom-song#song-builder")}>{t.actions.createSong}</a>
          <a href={localizedRoute(locale, "/")}>{t.nav.home}</a>
        </nav>
        <div className="header-tools">
          <label className="locale-control">
            <Globe2 aria-hidden="true" />
            <span className="sr-only">{t.languageLabel}</span>
            <select value={locale} onChange={(event) => changeLocale(event.target.value)} aria-label={t.languageLabel}>
              {localeOptions.map((option) => (
                <option key={option.code} value={option.code}>
                  {option.code.toUpperCase()}
                </option>
              ))}
            </select>
            <ChevronDown aria-hidden="true" />
          </label>
        </div>
      </header>

      <main id="top" className="mobile-storefront">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero__inner">
            <motion.div
              className="hero__content"
              initial={prefersReducedMotion ? undefined : { opacity: 0, y: 24 }}
              animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="hero__kicker">{locale === "en" ? "Custom songs. Made for you." : t.builder.kicker}</span>
              <h1 id="hero-title">
                {heroTitle.split("|").map((line, index, lines) => (
                  <span key={line}>
                    {line}
                    {index < lines.length - 1 ? <br /> : null}
                  </span>
                ))}
              </h1>
              <span className="accent-rule" aria-hidden="true" />
            </motion.div>
          </div>

          <dl className="hero__proof" aria-label="TuneTailor package highlights">
            {heroStats.map((proof) => {
              const Icon = proof.icon;
              return (
                <div key={proof.value}>
                  <Icon aria-hidden="true" />
                  <dt>{proof.value}</dt>
                  <dd>{proof.label}</dd>
                </div>
              );
            })}
          </dl>

          <motion.a
            className="button button--primary hero-cta"
            href={localizedRoute(locale, "/products/custom-song#song-builder")}
            whileTap={prefersReducedMotion ? undefined : { scale: 0.98 }}
            onClick={() => trackTuneTailorEvent("tunetailor_cta_clicked", { target: "hero_create" })}
          >
            {t.actions.createSong}
            <ArrowRight aria-hidden="true" />
          </motion.a>

          <article className="hero-player" aria-label={heroSample.title}>
            <img src="/images/tunetailor-for-my-dad-cover.png" alt="" aria-hidden="true" />
            <button className="hero-player__play" type="button" onClick={toggleHeroSample} aria-label={isHeroSamplePlaying ? "Pause sample" : "Play sample"}>
              {isHeroSamplePlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
            </button>
            <div className="hero-player__body">
              <strong>{locale === "en" ? "For My Dad" : heroSample.title}</strong>
              <small>{locale === "en" ? "Rock · Heartfelt" : heroSample.occasion}</small>
              <div className="hero-waveform" aria-hidden="true">
                {heroWaveform.map((height, index) => (
                  <span key={`${height}-${index}`} style={{ height }} />
                ))}
              </div>
              <div className="hero-player__meta">
                <span>0:00 / {heroSample.duration}</span>
                <span>{isHeroSamplePlaying ? "Now playing" : locale === "en" ? "Sample" : t.samples.featured}</span>
              </div>
            </div>
            <audio
              ref={heroAudioRef}
              preload="metadata"
              src={heroSample.audioUrl}
              onPlay={() => setIsHeroSamplePlaying(true)}
              onPause={() => setIsHeroSamplePlaying(false)}
              onEnded={() => setIsHeroSamplePlaying(false)}
            />
          </article>
        </section>

        {consent === "unknown" ? (
          <aside className="consent-banner" id="privacy-choices" aria-labelledby="consent-title">
            <div>
              <strong id="consent-title">{t.consent.title}</strong>
              <p>{t.consent.copy}</p>
            </div>
            <div className="consent-banner__actions">
              <button className="button button--plain" type="button" onClick={() => chooseAnalyticsConsent("denied")}>
                {t.consent.essentialOnly}
              </button>
              <button className="button button--primary" type="button" onClick={() => chooseAnalyticsConsent("granted")}>
                {t.consent.allow}
              </button>
            </div>
          </aside>
        ) : null}

        <motion.section className="builder-section" id="song-builder" aria-labelledby="builder-title">
          <form className="song-form" id="song-brief-form" onSubmit={submitSong}>
            <div className="storyline-step">
              <span className="step-badge">1</span>
              <div className="storyline-step__body">
                <div className="brief-heading">
                  <h2 id="builder-title">{locale === "en" ? "Song brief" : t.builder.title}</h2>
                  <p>{locale === "en" ? "Tell us about your story and vision." : t.builder.intro}</p>
                </div>

                <label className="form-field">
                  <span>{t.builder.titleLabel}</span>
                  <div className="input-shell">
                    <input
                      value={draft.title}
                      maxLength={80}
                      onChange={(event) => updateDraft("title", event.target.value)}
                      placeholder={locale === "en" ? "For My Dad" : t.builder.titlePlaceholder}
                      autoComplete="off"
                      required
                    />
                    <em>{draft.title.length.toLocaleString(languageTag)} / 80</em>
                  </div>
                </label>

                <label className="form-field">
                  <span>{t.builder.storyLabel}</span>
                  <div className="input-shell input-shell--textarea">
                    <textarea
                      value={draft.story}
                      maxLength={maxStoryLength}
                      rows={5}
                      onChange={(event) => updateDraft("story", event.target.value)}
                      placeholder={
                        locale === "en"
                          ? "My dad has always been my biggest supporter. He taught me how to work hard, be kind, and never give up. This song is for his birthday to thank him for everything."
                          : t.builder.storyPlaceholder
                      }
                      required
                    />
                    <em>{draft.story.trim().length.toLocaleString(languageTag)} / {maxStoryLength.toLocaleString(languageTag)}</em>
                  </div>
                </label>
                <p className="field-help">
                  <Lightbulb aria-hidden="true" />
                  {locale === "en" ? "Tip: Include specific memories or moments that stood out." : t.builder.storyHelp}
                </p>
              </div>
            </div>

            <div className="storyline-step">
              <span className="step-badge">2</span>
              <div className="storyline-step__body">
                <h2>{locale === "en" ? "Style" : t.builder.genreLabel}</h2>
                <div className="genre-pills" role="group" aria-label={t.builder.genreLabel}>
                  {primaryGenreChoices.map((genre) => (
                    <button
                      className={draft.genre === genre ? "genre-pill is-selected" : "genre-pill"}
                      key={genre}
                      type="button"
                      onClick={() => updateDraft("genre", genre)}
                    >
                      {draft.genre === genre ? <Check aria-hidden="true" /> : null}
                      {genre}
                    </button>
                  ))}
                  <label className="genre-more">
                    <span className="sr-only">{t.builder.genreLabel}</span>
                    <select
                      value={primaryGenreChoices.includes(draft.genre) ? "" : draft.genre}
                      onChange={(event) => {
                        if (event.target.value) {
                          updateDraft("genre", event.target.value);
                        }
                      }}
                    >
                      <option value="">More</option>
                      {genres
                        .filter((genre) => !primaryGenreChoices.includes(genre))
                        .map((genre) => (
                          <option key={genre} value={genre}>
                            {t.builder.genreLabels[genre as keyof typeof t.builder.genreLabels] || genre}
                          </option>
                        ))}
                    </select>
                    <ChevronDown aria-hidden="true" />
                  </label>
                </div>
              </div>
            </div>

            <div className="storyline-step add-ons" id="add-ons">
              <span className="step-badge">3</span>
              <div className="storyline-step__body">
                <h2>{locale === "en" ? "Add-ons" : t.builder.optionalAddOns}</h2>
                <div className="add-ons__grid">
                  {addOns.map((addOn) => {
                    const product = getProductByHandle(catalog, addOn.handle);
                    const variant = selectedVariant(product);
                    const isAvailable = Boolean(variant?.available);
                    const addOnCopy = t.builder.addOns[addOn.key];
                    const AddOnIcon =
                      addOn.key === "instrumental" ? AudioWaveform : addOn.key === "lyric_video" ? Music2 : SlidersHorizontal;
                    return (
                      <label key={addOn.key} className={isAvailable ? "add-on" : "add-on is-unavailable"}>
                        <span className="add-on__icon">
                          <AddOnIcon aria-hidden="true" />
                        </span>
                        <span className="add-on__copy">
                          <strong>{addOnCopy.label}</strong>
                          <small>{addOnCopy.description}</small>
                        </span>
                        <em>{variant ? formatMoney(variant.price, languageTag) : addOn.approvedPrice}</em>
                        <input
                          type="checkbox"
                          checked={draft.addOns.includes(addOn.key)}
                          disabled={!isAvailable}
                          onChange={() => toggleAddOn(addOn.key)}
                        />
                        <span className="toggle-ui" aria-hidden="true" />
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="storyline-step review-step" id="review">
              <span className="step-badge">4</span>
              <div className="storyline-step__body">
                <h2>{locale === "en" ? "Review" : t.review.title}</h2>
                <p>{locale === "en" ? "We'll review your brief and deliver your song." : t.review.intro}</p>
                <div className="review-list">
                  <AnimatePresence mode="popLayout">
                    {songs.length === 0 ? (
                      <motion.div
                        className="empty-review"
                        key="empty-review"
                        initial={prefersReducedMotion ? undefined : { opacity: 0, y: 12 }}
                        animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
                        exit={prefersReducedMotion ? undefined : { opacity: 0 }}
                      >
                        <Headphones aria-hidden="true" />
                        <p>{t.review.emptyTitle}</p>
                      </motion.div>
                    ) : (
                      songs.map((song, index) => (
                        <motion.article
                          className="review-item"
                          key={song.id}
                          layout
                          initial={prefersReducedMotion ? undefined : { opacity: 0, y: 14 }}
                          animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
                          exit={prefersReducedMotion ? undefined : { opacity: 0, y: -10 }}
                        >
                          <div>
                            <span className="review-item__index">
                              {t.review.songLabel} {index + 1}
                            </span>
                            <h3>{song.title}</h3>
                            <p>{t.builder.genreLabels[song.genre as keyof typeof t.builder.genreLabels] || song.genre}</p>
                          </div>
                          <p className="review-item__story">{song.story}</p>
                          {song.addOns.length > 0 ? (
                            <ul className="review-item__addons">
                              {song.addOns.map((key) => {
                                const addOnCopy = t.builder.addOns[key];
                                return addOnCopy ? <li key={key}>{addOnCopy.label}</li> : null;
                              })}
                            </ul>
                          ) : null}
                          <div className="review-item__actions">
                            <button type="button" className="icon-text-button" onClick={() => editSong(song)}>
                              <PenLine aria-hidden="true" />
                              {t.actions.edit}
                            </button>
                            <button type="button" className="icon-text-button" onClick={() => removeSong(song.id)}>
                              <Trash2 aria-hidden="true" />
                              {t.actions.remove}
                            </button>
                          </div>
                        </motion.article>
                      ))
                    )}
                  </AnimatePresence>
                </div>
                {formError ? (
                  <p className="form-error" role="alert">
                    {formError}
                  </p>
                ) : null}
                {checkoutError ? (
                  <p className="form-error" role="alert">
                    {checkoutError}
                  </p>
                ) : null}
                <div className="form-actions">
                  <button className="button button--primary" type="submit">
                    <ShoppingCart aria-hidden="true" />
                    {editingId ? t.actions.saveSong : t.actions.addToReview}
                  </button>
                  {songs.length > 0 ? (
                    <button className="button button--secondary" type="button" disabled={isCheckingOut} onClick={checkout}>
                      <Lock aria-hidden="true" />
                      {isCheckingOut ? t.actions.openingCheckout : t.actions.proceedToCheckout}
                    </button>
                  ) : null}
                  {editingId ? (
                    <button
                      className="button button--plain"
                      type="button"
                      onClick={() => {
                        setDraft(emptyDraft);
                        setEditingId(null);
                        setFormError("");
                      }}
                    >
                      {t.actions.cancelEdit}
                    </button>
                  ) : null}
                </div>
                <p className="catalog-status">
                  <CheckCircle2 aria-hidden="true" />
                  {t.status[catalogStatus]}
                </p>
              </div>
            </div>
          </form>
        </motion.section>

        <motion.section className="process-section" id="how-it-works" aria-labelledby="process-title" {...revealMotion}>
          <div className="section-heading section-heading--split">
            <div>
              <span className="kicker">{t.process.kicker}</span>
              <h2 id="process-title">{t.process.title}</h2>
            </div>
            <p>{t.process.intro}</p>
          </div>
          <div className="process-grid">
            {t.process.steps.map((step, index) => (
              <article className="process-step" key={step.title}>
                <span>{index + 1}</span>
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </article>
            ))}
          </div>
        </motion.section>

        <motion.section className="examples-section" id="examples" aria-labelledby="examples-title" {...revealMotion}>
          <div className="section-heading section-heading--split">
            <div>
              <span className="kicker">{t.samples.kicker}</span>
              <h2 id="examples-title">{t.samples.title}</h2>
            </div>
            <p>{t.samples.intro}</p>
          </div>
          <div className="sample-showcase">
            <article className="sample-stage" aria-live="polite">
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeSample.artworkUrl}
                  src={activeSample.artworkUrl}
                  alt={`${activeSample.title} sample artwork`}
                  loading="lazy"
                  decoding="async"
                  initial={prefersReducedMotion ? undefined : { opacity: 0, scale: 1.03 }}
                  animate={prefersReducedMotion ? undefined : { opacity: 1, scale: 1 }}
                  exit={prefersReducedMotion ? undefined : { opacity: 0 }}
                  transition={{ duration: 0.32 }}
                />
              </AnimatePresence>
              <AnimatePresence mode="wait">
                <motion.div
                  className="sample-stage__body"
                  key={activeSample.title}
                  initial={prefersReducedMotion ? undefined : { opacity: 0, y: 12 }}
                  animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0 }}
                  exit={prefersReducedMotion ? undefined : { opacity: 0, y: -8 }}
                  transition={{ duration: 0.28 }}
                >
                  <span className="kicker">{t.samples.featured}</span>
                  <h3>{activeSample.title}</h3>
                  <p className="sample-stage__meta">
                    {activeSample.occasion} · {activeSample.duration}
                  </p>
                  <p>{activeSample.description}</p>
                  <audio
                    controls
                    preload="metadata"
                    src={activeSample.audioUrl}
                    onPlay={() => trackTuneTailorEvent("tunetailor_sample_played", { track: activeSample.title })}
                  />
                </motion.div>
              </AnimatePresence>
            </article>
            <div className="sample-browser">
              <div className="sample-browser__topline">
                <span>{t.samples.choose}</span>
                <div className="sample-controls">
                  <button type="button" onClick={showPreviousSample} aria-label="Previous sample">
                    <ChevronLeft aria-hidden="true" />
                  </button>
                  <button type="button" onClick={showNextSample} aria-label="Next sample">
                    <ChevronRight aria-hidden="true" />
                  </button>
                </div>
              </div>
              <div className="sample-rail" aria-label={t.samples.choose}>
                <div className="sample-rail__viewport" ref={sampleRailRef}>
                  <div className="sample-rail__track">
                    {sampleTracks.map((track, index) => (
                      <button
                        className={index === activeSampleIndex ? "sample-thumb is-active" : "sample-thumb"}
                        key={track.title}
                        type="button"
                        onClick={() => setActiveSampleIndex(index)}
                      >
                        <img src={track.artworkUrl} alt="" aria-hidden="true" loading="lazy" decoding="async" />
                        <span>
                          <strong>{track.title}</strong>
                          <small>
                            {track.occasion} · {track.duration}
                          </small>
                        </span>
                        {index === activeSampleIndex ? <PlayCircle aria-hidden="true" /> : null}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        <motion.section className="occasions-section" id="occasions" aria-labelledby="occasions-title" {...revealMotion}>
          <div className="section-heading">
            <span className="kicker">{t.occasions.kicker}</span>
            <h2 id="occasions-title">{t.occasions.title}</h2>
            <p>{t.occasions.intro}</p>
          </div>
          <div className="occasion-layout">
            <img src="/images/tunetailor-occasions.jpg" alt="Custom song scenes for life events" loading="lazy" decoding="async" />
            <div className="occasion-grid">
              {t.occasions.items.map((occasion) => (
                <article className="occasion-card" key={occasion.title}>
                  <h3>{occasion.title}</h3>
                  <p>{occasion.copy}</p>
                </article>
              ))}
            </div>
          </div>
        </motion.section>

        <section className="trust-section" id="quality-control" aria-label="TuneTailor guarantees">
          {t.trust.items.map((item, index) => {
            const Icon = index === 0 ? ShieldCheck : index === 1 ? Gift : Sparkles;
            return (
              <div key={item.title}>
                <Icon aria-hidden="true" />
                <strong>{item.title}</strong>
                <span>{item.copy}</span>
              </div>
            );
          })}
        </section>

        <motion.section className="faq-section" id="faq" aria-labelledby="faq-title" {...revealMotion}>
          <div className="section-heading">
            <span className="kicker">{t.faq.kicker}</span>
            <h2 id="faq-title">{t.faq.title}</h2>
          </div>
          <div className="faq-list">
            {t.faq.items.map((item) => (
              <details key={item.question}>
                <summary>
                  {item.question}
                  <ChevronRight aria-hidden="true" />
                </summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </motion.section>
      </main>

      {showMobileCheckout ? (
        <aside className="mobile-checkout" aria-label={t.review.estimatedTotal}>
          <div className="mobile-checkout__summary">
            <span>{t.review.signatureSong}</span>
            <strong>{formatMoney({ amount: stickyAmount.toFixed(2), currencyCode }, languageTag)}</strong>
            <ChevronDown aria-hidden="true" />
          </div>
          <button className="button button--primary mobile-checkout__button" type="button" disabled={isCheckingOut} onClick={handleStickyAction}>
            {isCheckingOut ? t.actions.openingCheckout : stickyActionLabel}
            <ArrowRight aria-hidden="true" />
          </button>
          <p>
            <Lock aria-hidden="true" />
            {locale === "en" ? "Secure checkout" : t.review.secureCheckout}
          </p>
        </aside>
      ) : null}

      <footer className="site-footer" id="contact">
        <div>
          <a className="brand" href={localizedRoute(locale, "/")} aria-label="TuneTailor home">
            <span className="brand-mark" aria-hidden="true">
              TT
            </span>
            <span>TuneTailor</span>
          </a>
          <p>{t.footer.tagline}</p>
        </div>
        <div>
          <h2>{t.footer.store}</h2>
          <a href={localizedRoute(locale, "/pages/examples")}>{t.nav.examples}</a>
          <a href={localizedRoute(locale, "/products/custom-song#song-builder")}>{t.actions.createSong}</a>
          <a href={localizedRoute(locale, "/pages/how-it-works")}>{t.nav.howItWorks}</a>
          <a href={localizedRoute(locale, "/pages/faq")}>{t.nav.faq}</a>
        </div>
        <div>
          <h2>{t.nav.contact}</h2>
          <a href="mailto:support@tunetailor.org">
            <Mail aria-hidden="true" />
            support@tunetailor.org
          </a>
          <a href="/policies/privacy-policy">Privacy</a>
          <a href="/policies/refund-policy">Refunds</a>
          <a href="/policies/terms-of-service">Terms</a>
          <button className="footer-link-button" type="button" onClick={reopenAnalyticsChoices}>
            {t.consent.choices}
          </button>
        </div>
      </footer>
    </>
  );
}

function createSongId() {
  if (crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `tt-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function detectLocale(): LocaleCode {
  const routedLocale = localeFromPath();
  if (routedLocale) {
    return routedLocale;
  }

  try {
    const savedLocale = localStorage.getItem("tunetailor:locale");
    if (savedLocale && isLocaleCode(savedLocale)) {
      return savedLocale;
    }
  } catch {
    // Fall through to the browser language.
  }

  const browserLocale = navigator.language.split("-")[0];
  return isLocaleCode(browserLocale) ? browserLocale : defaultLocale;
}
